import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Globe, X, Loader2, Check } from 'lucide-react'
import { fetchSitesAvailableForTask, assignSiteToTask, fetchSiteSources, fetchTaskSources, saveTaskSources } from '../../../slice/sharepointSlice'

// Agent-side "+ Add site" wizard, reused for "Edit sources" on an already-added site.
// Step 1 (skipped in edit mode via `initialSite`): pick one org-connected
// site, grouped by Knowledge Area.
// Step 2: pick which document libraries / Lists ("sources") to use — nothing
// is imported by saving either step, an import only runs when explicitly started.
const AddSharePointSiteModal = ({ orgId, taskId, aispaceColor, t, onClose, onAdded, initialSite = null }) => {
  const dispatch = useDispatch()
  const {
    sitesAvailableForTask, sitesAvailableForTaskLoading, assignSiteLoading,
    siteSources, siteSourcesLoading, saveSourcesLoading, taskSources,
  } = useSelector(state => state.sharepoint)

  const [step, setStep] = useState(initialSite ? 'sources' : 'site') // 'site' | 'sources'
  const [selectedSite, setSelectedSite] = useState(initialSite)
  const [selectedSourceKeys, setSelectedSourceKeys] = useState(new Set()) // `${type}:${id}`

  useEffect(() => {
    if (!initialSite && orgId && taskId) dispatch(fetchSitesAvailableForTask({ orgId, taskId }))
  }, [initialSite, orgId, taskId, dispatch])

  useEffect(() => {
    if (initialSite && orgId && taskId) {
      dispatch(fetchSiteSources({ orgId, siteId: initialSite.id }))
      dispatch(fetchTaskSources({ orgId, siteId: initialSite.id, taskId }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (initialSite && taskSources) {
      setSelectedSourceKeys(new Set(taskSources.map(s => `${s.source_type}:${s.source_id}`)))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskSources])

  const groups = []
  const noAreaSites = (sitesAvailableForTask || []).filter(s => !(s.knowledge_area_names || []).length)
  if (noAreaSites.length > 0) {
    groups.push({ label: t('agentSettings.filesAddSiteNoKnowledgeArea'), sites: noAreaSites })
  }
  const seenDeptNames = new Set()
  ;(sitesAvailableForTask || []).forEach(site => {
    (site.knowledge_area_names || []).forEach(name => {
      if (seenDeptNames.has(name)) return
      seenDeptNames.add(name)
      groups.push({
        label: name,
        sites: (sitesAvailableForTask || []).filter(s => (s.knowledge_area_names || []).includes(name)),
      })
    })
  })

  const handleNext = () => {
    if (!selectedSite) return
    setSelectedSourceKeys(new Set())
    setStep('sources')
    dispatch(fetchSiteSources({ orgId, siteId: selectedSite.id }))
  }

  const toggleSource = (type, id) => {
    const key = `${type}:${id}`
    setSelectedSourceKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleAddSite = async () => {
    if (!selectedSite) return
    try {
      if (!initialSite) {
        await dispatch(assignSiteToTask({ orgId, siteId: selectedSite.id, taskId })).unwrap()
      }
      const libraries = siteSources?.libraries || []
      const lists = siteSources?.lists || []
      const sources = []
      libraries.forEach(l => {
        if (selectedSourceKeys.has(`library:${l.id}`)) {
          sources.push({ source_type: 'library', source_id: l.id, source_name: l.name })
        }
      })
      lists.forEach(l => {
        if (selectedSourceKeys.has(`list:${l.id}`)) {
          sources.push({ source_type: 'list', source_id: l.id, source_name: l.name })
        }
      })
      // Always save in edit mode (even an empty selection clears sources);
      // for a fresh add, skip the call entirely when nothing was picked.
      if (sources.length > 0 || initialSite) {
        await dispatch(saveTaskSources({ orgId, siteId: selectedSite.id, taskId, sources })).unwrap()
      }
      onAdded()
    } catch (err) {
      // assignSiteError / saveSourcesError are surfaced via redux state
    }
  }

  const libraries = siteSources?.libraries || []
  const lists = siteSources?.lists || []

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-[560px] shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-start justify-between px-6 py-5">
          <div>
            <h2 className="text-[20px] font-bold text-[#111827]">
              {step === 'site' ? t('agentSettings.filesAddSiteTitle') : t('agentSettings.filesAddSiteSourcesTitle', { siteName: selectedSite?.site_name || '' })}
            </h2>
            <p className="text-[13px] text-[#6B7280] mt-1">
              {step === 'site' ? t('agentSettings.filesAddSiteSubtitle') : t('agentSettings.filesAddSiteSourcesSubtitle')}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        <div className="px-6 pb-4 flex-1 overflow-y-auto space-y-5">
          {step === 'site' ? (
            sitesAvailableForTaskLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
              </div>
            ) : groups.length === 0 ? (
              <p className="text-[14px] text-[#6B7280] text-center py-12">{t('agentSettings.filesAddSiteEmpty')}</p>
            ) : (
              groups.map(group => (
                <div key={group.label}>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{group.label}</p>
                  <div className="space-y-2">
                    {group.sites.map(site => {
                      const isSelected = selectedSite?.id === site.id
                      return (
                        <button
                          key={site.id}
                          onClick={() => setSelectedSite(site)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors hover:bg-[#F9FAFB]"
                          style={{ borderColor: isSelected ? aispaceColor : '#E5E7EB', backgroundColor: isSelected ? `${aispaceColor}0D` : 'white' }}
                        >
                          <Globe size={18} className="text-[#6B7280] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-semibold text-[#111827] truncate">{site.site_name}</p>
                            <p className="text-[12px] text-[#6B7280] truncate">{site.site_url}</p>
                          </div>
                          {isSelected && <Check size={18} className="flex-shrink-0" style={{ color: aispaceColor }} />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))
            )
          ) : siteSourcesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
            </div>
          ) : (
            <>
              {libraries.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('agentSettings.filesAddSiteDocumentLibraries')}</p>
                  <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
                    {libraries.map(lib => {
                      const key = `library:${lib.id}`
                      const checked = selectedSourceKeys.has(key)
                      return (
                        <label key={lib.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F9FAFB]">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSource('library', lib.id)}
                            className="w-4 h-4 rounded border-[#D1D5DB]"
                            style={{ accentColor: aispaceColor }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-medium text-[#111827] truncate">{lib.name}</p>
                            {lib.item_count != null && (
                              <p className="text-[12px] text-[#6B7280]">{t('agentSettings.filesAddSiteItemCount', { count: lib.item_count })}</p>
                            )}
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}

              {lists.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('agentSettings.filesAddSiteLists')}</p>
                  {/* Lists can't be imported yet — Graph can't download SharePoint
                      list-item attachments without a per-tenant certificate-based
                      auth flow most orgs haven't set up (see
                      SharePointService.get_site_sources). Selection is disabled
                      here rather than just hinted, since letting it through used
                      to silently import the site's unrelated default library
                      instead of the chosen list. */}
                  <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB] opacity-60">
                    {lists.map(list => (
                      <label key={list.id} className="flex items-center gap-3 px-4 py-3 cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={false}
                          disabled
                          className="w-4 h-4 rounded border-[#D1D5DB] cursor-not-allowed"
                        />
                        <p className="text-[14px] font-medium text-[#111827] truncate flex-1">{list.name}</p>
                      </label>
                    ))}
                  </div>
                  <p className="text-[12px] text-[#9CA3AF] mt-2">{t('agentSettings.filesAddSiteListsHint')}</p>
                </div>
              )}

              {libraries.length === 0 && lists.length === 0 && (
                <p className="text-[14px] text-[#6B7280] text-center py-12">{t('agentSettings.filesAddSiteNoSources')}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          {step === 'sources' ? (
            <p className={`text-[12px] max-w-[220px] ${selectedSourceKeys.size === 0 ? 'text-[#D97706] font-medium' : 'text-[#9CA3AF]'}`}>
              {selectedSourceKeys.size === 0 ? t('agentSettings.filesAddSiteSelectAtLeastOne') : t('agentSettings.filesAddSiteSourcesFooterHint')}
            </p>
          ) : <span />}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            >
              {t('common.cancel')}
            </button>
            {step === 'site' ? (
              <button
                onClick={handleNext}
                disabled={!selectedSite}
                className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('agentSettings.filesAddSiteNext')}
              </button>
            ) : (
              <button
                onClick={handleAddSite}
                disabled={assignSiteLoading || saveSourcesLoading || selectedSourceKeys.size === 0}
                className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
                style={{ backgroundColor: aispaceColor }}
              >
                {(assignSiteLoading || saveSourcesLoading) && <Loader2 size={14} className="animate-spin" />}
                {t('agentSettings.filesAddSiteSubmit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddSharePointSiteModal
